import {
  isSageService,
  matchSageUnit,
  parseSageSnapshot,
  SageReconciliationMatch,
  sageSnapshotName,
  UnitOfMeasureOption,
} from './sage-integration.model';
import { UnitNameHit } from './sage-unit-suggest.model';
import { groupUnit, UnitGroup, UnitGroupKind } from './sage-wizard.model';

/** Shared by the first-sync wizard's unit cards and the console's units panel, which differ only in look. */

export const UNIT_GROUP_PAGE_SIZE = 50;

export interface UnitGroupItem {
  matchId: number;
  name: string;
  code: string;
  itemTypeId: number;
  isService: boolean;
  uomId: number | null;
  hits: UnitNameHit[];
  /** Why the last Apply refused it. */
  reason: string | null;
}

export interface UnitGroupView {
  key: string;
  kind: UnitGroupKind;
  sageUnit: string;
  items: UnitGroupItem[];
  /** The unit the group's picker offers before anything is chosen: read from the names, or Sage's unit now in Aivor. */
  suggestedUnit: UnitOfMeasureOption | null;
  /** The unit more than half the items have. */
  groupUomId: number | null;
  withUnit: number;
  ownUnit: number;
  answered: boolean;
  /** An item whose name has a number other than 1, for the purchase quantity note. */
  quantityExample: string | null;
}

export interface NamePart {
  text: string;
  found: boolean;
}

const unitAnswered = (m: SageReconciliationMatch) =>
  (m.decision !== 'ImportToAivor' && m.decision !== 'NeedsUomMapping') || !!m.resolvedUOMID;

export function toUnitGroupView(
  group: UnitGroup,
  units: UnitOfMeasureOption[],
  reasons: ReadonlyMap<number, string> = new Map()
): UnitGroupView {
  const items = group.matches.map((m) => {
    const raw = parseSageSnapshot(m.sageRecordSnapshot);
    return {
      matchId: m.id,
      name: sageSnapshotName(m.sageRecordSnapshot, 'Item'),
      code: raw?.Code ?? '',
      itemTypeId: m.resolvedItemTypeID ?? 1,
      isService: isSageService(raw),
      uomId: m.resolvedUOMID ?? null,
      hits: group.suggestions.get(m.id)?.hits ?? [],
      reason: reasons.get(m.id) ?? null,
    };
  });
  const groupUomId = groupUnit(group.matches);
  return {
    key: group.key,
    kind: group.kind,
    sageUnit: group.sageUnit,
    items,
    suggestedUnit: group.unit ?? (group.kind === 'sage-unit' ? matchSageUnit(group.sageUnit, units)?.option ?? null : null),
    groupUomId,
    withUnit: items.filter((i) => i.uomId != null).length,
    ownUnit: groupUomId == null ? 0 : items.filter((i) => i.uomId != null && i.uomId !== groupUomId).length,
    answered: group.matches.every(unitAnswered),
    quantityExample: items.find((i) => i.hits.some((h) => h.quantity != null && h.quantity !== 1))?.name ?? null,
  };
}

export function unitGroupTitle(group: UnitGroupView): string {
  const n = group.items.length;
  switch (group.kind) {
    case 'suggested': {
      const unit = group.suggestedUnit?.name ?? '';
      return n === 1 ? `1 item looks like ${unit} from its name` : `${n} items look like ${unit} from their names`;
    }
    case 'sage-unit':
      return `${n} ${n === 1 ? 'item uses' : 'items use'} "${group.sageUnit}" in Sage`;
    default:
      return `${n} ${n === 1 ? 'item has' : 'items have'} no unit in Sage or in ${n === 1 ? 'its name' : 'their names'}`;
  }
}

export function unitGroupFieldLabel(group: UnitGroupView): string {
  const n = group.items.length;
  return n === 1 ? 'Unit in Aivor for this item' : `Unit in Aivor for these ${n} items`;
}

/** The suggestion shows in the picker until the user answers; nothing is saved for it yet. */
export function unitGroupShownUomId(group: UnitGroupView): number | null {
  if (group.groupUomId != null) return group.groupUomId;
  return group.suggestedUnit && !group.answered ? group.suggestedUnit.id : null;
}

/** Every item has a unit and none is on most of them: a group pick would only replace their own
 * units, so it saves nothing and says so (decided 26/10/2026). */
export function unitGroupAllOwn(group: UnitGroupView): boolean {
  return group.groupUomId == null && group.items.length > 0 && group.items.every((i) => i.uomId != null);
}

export const UNIT_GROUP_ALL_OWN_NOTE = 'Each item already has its own unit, so nothing changed. Change them in the list below.';

export function unitGroupUseLabel(group: UnitGroupView): string | null {
  if (!group.suggestedUnit || group.groupUomId != null) return null;
  const n = group.items.length;
  const left = group.items.filter((i) => i.uomId == null).length;
  if (!left) return null;
  const name = group.suggestedUnit.name;
  if (left < n) return `Use ${name} for the other ${left}`;
  return n === 1 ? `Use ${name} for this item` : `Use ${name} for these ${n} items`;
}

export function unitGroupStatus(group: UnitGroupView): string | null {
  if (!group.withUnit) return null;
  const own = group.ownUnit ? ` · ${group.ownUnit} ${group.ownUnit === 1 ? 'has its' : 'have their'} own unit` : '';
  return `${group.withUnit} of ${group.items.length} have a unit${own}`;
}

export function hasOwnUnit(group: UnitGroupView, item: UnitGroupItem): boolean {
  return item.uomId != null && group.groupUomId != null && item.uomId !== group.groupUomId;
}

export function filterUnitGroupItems(items: UnitGroupItem[], query: string): UnitGroupItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((i) => i.name.toLowerCase().includes(q) || i.code.toLowerCase().includes(q));
}

export function nameParts(name: string, hits: UnitNameHit[]): NamePart[] {
  const parts: NamePart[] = [];
  let at = 0;
  for (const hit of hits) {
    if (hit.start > at) parts.push({ text: name.slice(at, hit.start), found: false });
    parts.push({ text: name.slice(hit.start, hit.end), found: true });
    at = hit.end;
  }
  if (at < name.length) parts.push({ text: name.slice(at), found: false });
  return parts;
}

/** The wizard runs before anything is in Aivor; the console's panel after. */
export function quantityNote(example: string, during: 'first-sync' | 'later'): string {
  const when = during === 'first-sync' ? 'after the first sync' : "once they're in Aivor";
  return `Purchase quantity stays 1. Items like "${example}" need their quantity changed on the item ${when}, or their cost per unit will be wrong.`;
}
